import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Database, ShieldCheck, Calculator, FileSpreadsheet } from 'lucide-react';
import { formatDateShort, formatDateTime } from '@/domain/time';
import { DEMO_NOW_UTC, DEMO_WINDOW, SEED_VERSION } from '@/fixtures/scenarios';
import { presentText } from '@/lib/present';

/**
 * Én diskret, samlet kildeopplysning for hele portalen:
 * hva tallene representerer, hvordan de beregnes og hva prototypen ikke gjør.
 */
export function AboutDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="about-portal-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Om portalen</DialogTitle>
          <DialogDescription>Produksjonsportal for Hardanger Rock, Dimmelsvik. Prototype for analyse av produksjon, maskinbruk og diesel.</DialogDescription>
        </DialogHeader>
        <dl className="space-y-4 text-sm leading-6">
          <div className="flex gap-3">
            <Database className="mt-1 h-4 w-4 shrink-0 text-[var(--hr-accent-text)]" aria-hidden="true" />
            <div>
              <dt className="font-semibold text-[var(--hr-heading)]">Datagrunnlag: eksempeldata</dt>
              <dd className="text-[var(--hr-muted)]" data-testid="about-data-basis">
                Tallene er et deterministisk eksempeldatasett ({presentText(SEED_VERSION)}) for perioden {formatDateShort(DEMO_WINDOW.from)} – {formatDateShort(DEMO_WINDOW.to)}, med fast referansetidspunkt {formatDateTime(DEMO_NOW_UTC)}. Ingen kundesystemer er koblet til, og ingen tall er Hardanger Rocks faktiske driftstall. Kvalitetsvarsler, enheter og usikkerhet vises som de ville gjort med reelle kilder.
              </dd>
            </div>
          </div>
          <div className="flex gap-3">
            <Calculator className="mt-1 h-4 w-4 shrink-0 text-[var(--hr-accent-text)]" aria-hidden="true" />
            <div>
              <dt className="font-semibold text-[var(--hr-heading)]">Én felles beregningsmotor</dt>
              <dd className="text-[var(--hr-muted)]">Alle nøkkeltall, grafer, rapporter, eksporter og assistentens svar beregnes fra samme kildeposter. Forholdstall er summert teller delt på summert nevner; manglende påkrevde felt gjør forholdstallet utilgjengelig i stedet for å gjette.</dd>
            </div>
          </div>
          <div className="flex gap-3">
            <FileSpreadsheet className="mt-1 h-4 w-4 shrink-0 text-[var(--hr-accent-text)]" aria-hidden="true" />
            <div>
              <dt className="font-semibold text-[var(--hr-heading)]">Antatte satser</dt>
              <dd className="text-[var(--hr-muted)]">Kostnad og CO₂ bruker konfigurerbare, antatte satser – ikke kundens avtalte satser. De er merket «illustrativ» der de vises.</dd>
            </div>
          </div>
          <div className="flex gap-3">
            <ShieldCheck className="mt-1 h-4 w-4 shrink-0 text-[var(--hr-accent-text)]" aria-hidden="true" />
            <div>
              <dt className="font-semibold text-[var(--hr-heading)]">Demo-inngang</dt>
              <dd className="text-[var(--hr-muted)]" data-testid="about-auth-note">
                Portalen åpnes med «Åpne demo» – det finnes ingen konto, passord eller tilgangsstyring i forhåndsvisningen. Økten lagres kun i nettleserens sessionStorage; ingen hemmeligheter samles inn eller lagres. Demoprofilen i Min dag velger bare hvilken fiktiv medarbeider som vises og er ikke en sikkerhetsgrense. Før reelle data tas i bruk må en backend håndheve organisasjon, rolle og sesjon.
              </dd>
            </div>
          </div>
        </dl>
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="about-portal-close-button">
            Lukk
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
